import { stringNormalize } from '../stringNormalize'

import { BlankByBlankGrading, CueTemplate, PartialResponse } from '../../interfaces'
import { feedbackStrings } from '../constants/feedback_strings'
import { conceptResultTemplate } from '../helpers/concept_result_template'

const CAPITALIZATION_CONCEPT_UID = '66upe3S5uvqxuHoHOt4PcQ'

export function blankByBlankMatch(
  blankByBlankGrading: BlankByBlankGrading,
  response: string,
  inputVals: string[],
  caseInsensitive: boolean,
  maxAttempts
): PartialResponse | undefined {
  // Get the number of blanks from the grading object
  const blankKeys = Object.keys(blankByBlankGrading).sort()
  const numberOfBlanks = blankKeys.length

  if (numberOfBlanks !== inputVals.length) {
    // Number of blanks doesn't match number of inputs
    return undefined
  }

  const matches: Array<{ match: CueTemplate; inputVal: string; blankNumber: number }> = []

  // Check each blank sequentially
  for (let i = 0; i < numberOfBlanks; i++) {
    const blankKey = blankKeys[i]
    const inputVal = inputVals[i]?.trim()
    const cueTemplates = blankByBlankGrading[blankKey]
    const blankNumber = i + 1

    if (!cueTemplates) {
      continue
    }

    // Find matching cue template for this blank
    const match = findMatchingCueTemplate(inputVal, cueTemplates, caseInsensitive)

    if (!match) {
      // Input doesn't match any cue for this blank - return undefined
      // The calling code will handle showing appropriate feedback
      return undefined
    }

    // If the match is non-optimal, return its feedback immediately
    if (!match.optimal) {
      const caseCorrectedResponse = caseInsensitive
        ? buildCaseCorrectedResponse(response, blankByBlankGrading, inputVals)
        : response

      return {
        author: `Blank ${blankNumber} Grading`,
        concept_results: match.conceptResults,
        feedback: match.feedback,
        text: caseCorrectedResponse,
      }
    }

    // Store the match for later capitalization check
    matches.push({ match, inputVal, blankNumber })
  }

  // All blanks are optimal - now check capitalization if case insensitive AND there is more than one attempt possible
  if (caseInsensitive && maxAttempts > 1) {
    for (const { match, inputVal, blankNumber } of matches) {
      const capitalizationMatch = checkCapitalization(inputVal, match.cue)
      if (!capitalizationMatch) {
        // Capitalization doesn't match for this blank
        // Include both the concept results from the match AND the capitalization error
        const conceptResults = [
          ...(match.conceptResults || []),
          conceptResultTemplate(CAPITALIZATION_CONCEPT_UID, false),
        ]
        return {
          author: 'Capitalization Hint',
          concept_results: conceptResults,
          feedback: feedbackStrings.caseError,
          // we leave `response` alone here; this path is just for capitalization hints
        }
      }
    }
  }

  // All blanks are optimal and capitalization is correct (or case sensitive mode, or capitalization is incorrect but only one attempt was possible)
  // Return feedback from the last blank
  const lastMatchData = matches[matches.length - 1]
  if (lastMatchData) {
    return {
      author: `Blank ${lastMatchData.blankNumber} Grading`,
      concept_results: lastMatchData.match.conceptResults,
      feedback: lastMatchData.match.feedback,
      optimal: true,
    }
  }

  return undefined
}

export function findMatchingCueTemplate(
  input: string,
  cueTemplates: CueTemplate[],
  caseInsensitive: boolean
): CueTemplate | undefined {
  const normalizedInput = stringNormalize(input)

  return cueTemplates.find(template => {
    const normalizedCue = stringNormalize(template.cue)

    if (caseInsensitive) {
      return normalizedInput.toLowerCase() === normalizedCue.toLowerCase()
    }

    return normalizedInput === normalizedCue
  })
}

export function checkCapitalization(input: string, expected: string): boolean {
  return stringNormalize(input) === stringNormalize(expected)
}

function buildCaseCorrectedResponse(
  response: string,
  blankByBlankGrading: BlankByBlankGrading,
  inputVals: string[]
): string {
  const blankKeys = Object.keys(blankByBlankGrading).sort()
  let correctedResponse = response

  for (let i = 0; i < blankKeys.length; i++) {
    const blankKey = blankKeys[i]
    const rawInputVal = inputVals[i]
    const inputVal = rawInputVal?.trim()
    const cueTemplates = blankByBlankGrading[blankKey]

    if (!inputVal || !cueTemplates) {
      continue
    }

    const match = findMatchingCueTemplate(inputVal, cueTemplates, true)

    // Only “case correct” when the cue matches ignoring case, but differs in capitalization
    if (match && !checkCapitalization(inputVal, match.cue)) {
      const pattern = new RegExp(escapeRegExp(inputVal))
      correctedResponse = correctedResponse.replace(pattern, match.cue)
    }
  }

  return correctedResponse
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
